import { memo } from 'react';
import { Box, Button, CircularProgress, Dialog, IconButton, InputLabel, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '~/assets/icons';

export interface BaseDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  cccd: string;
  fullName: string;
  children: React.ReactNode;
  onConfirm: () => void | Promise<void>;
  isConfirmLoading?: boolean;
  cancelText?: string;
  confirmText?: string;
  confirmButtonClassName?: string;
}

const BaseDeviceModal = ({
  isOpen,
  onClose,
  title,
  cccd,
  fullName,
  children,
  onConfirm,
  isConfirmLoading = false,
  cancelText,
  confirmText,
  confirmButtonClassName,
}: BaseDeviceModalProps) => {
  const { t } = useTranslation('admin/common');
  const resolvedCancelText = cancelText ?? t('cancel');
  const resolvedConfirmText = confirmText ?? t('confirm');
  const baseButtonTextClassName =
    "!font-['Manrope', 'sans-serif'] !font-semibold !text-base !leading-[140%] !tracking-[0.02em] !normal-case";
  const confirmHoverClassName = confirmButtonClassName ? '' : 'hover:!bg-[#0E2F58]';

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        className: '!w-full !max-w-[640px] !rounded-[12px] !pt-4 !px-4 !pb-7 !m-4 !bg-white',
      }}
    >
      <Box className='flex flex-col gap-5 w-full'>
        <Box component='header' className='flex items-center justify-between gap-3 w-full'>
          <Typography component='h2' className='font-semibold text-xl leading-[140%] tracking-normal text-[#161A1F]'>
            {title}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label='close-modal'
            size='small'
            className='!p-[2px] !text-[#7B8794] [&_svg]:w-[18px] [&_svg]:h-[18px]'
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component='section' className='flex flex-col gap-4 w-full'>
          <Box className='flex flex-col gap-2 w-full'>
            <InputLabel
              shrink={false}
              className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
            >
              {t('deviceModal.citizenIdLabel')}
            </InputLabel>
            <Box className='w-full h-12 rounded-[12px] px-[14px] flex items-center border-2 border-[#D7DEE7] bg-[#D7DEE7] font-normal text-sm leading-[140%] tracking-[0.02em] text-[#7B8794] select-text'>
              {cccd}
            </Box>
          </Box>

          <Box className='flex flex-col gap-2 w-full'>
            <InputLabel
              shrink={false}
              className='static !transform-none h-5 flex items-center font-medium text-sm leading-[140%] tracking-[0.02em] text-[#161A1F] mb-0'
            >
              {t('deviceModal.fullNameLabel')}
            </InputLabel>
            <Box className='w-full h-12 rounded-[12px] px-[14px] flex items-center border-2 border-[#D7DEE7] bg-[#D7DEE7] font-normal text-sm leading-[140%] tracking-[0.02em] text-[#7B8794] select-text'>
              {fullName}
            </Box>
          </Box>

          <Box className='w-full'>{children}</Box>
        </Box>

        <Box component='footer' className='flex gap-4 w-full'>
          <Button
            onClick={onClose}
            className={`!flex-1 !h-12 !rounded-[12px] !py-2 !px-8 !bg-[#D7DEE7] hover:!bg-[#C8D1DC] !text-[#404854] !shadow-none !transition-colors ${baseButtonTextClassName}`}
          >
            {resolvedCancelText}
          </Button>

          <Button
            onClick={onConfirm}
            disabled={isConfirmLoading}
            className={`!flex-1 !h-12 !rounded-[12px] !py-2 !px-8 !bg-[#123B6D] ${confirmHoverClassName} !text-white !shadow-none !transition-colors ${baseButtonTextClassName} ${confirmButtonClassName ?? ''}`}
          >
            {isConfirmLoading ? <CircularProgress size={20} className='!text-white' /> : resolvedConfirmText}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default memo(BaseDeviceModal);
